import type { SceneChoice } from '../games/scenes/catalog';
import { SCENES } from '../games/scenes/scenes';
import { SceneView } from '../games/scenes/SceneView';
import { useT } from '../i18n/i18n';

export default function SceneEditor({ label, hint, partsLabel, scenes, scene, parts, onChange }: {
  label: string;
  hint?: string;
  partsLabel: string;
  scenes: readonly SceneChoice[];
  scene: string;
  parts: string[];
  onChange: (next: { scene: string; parts: string[] }) => void;
}) {
  const t = useT();
  const current = scenes.find((s) => s.id === scene) ?? scenes[0];
  const picture = SCENES[current.id];
  const toggle = (id: string) => {
    const next = parts.includes(id) ? parts.filter((p) => p !== id) : [...parts, id];
    onChange({ scene: current.id, parts: current.parts.map((p) => p.id).filter((p) => next.includes(p)) });
  };
  return (
    <fieldset className="field scene-editor">
      <legend className="field-label">{t(label)}</legend>
      {hint && <p className="field-hint">{t(hint)}</p>}
      {scenes.length > 1 && (
        <div className="field">
          <label htmlFor="scene-choice">{t('Picture')}</label>
          <select
            id="scene-choice"
            value={current.id}
            onChange={(e) => {
              const s = scenes.find((x) => x.id === e.target.value) ?? scenes[0];
              onChange({ scene: s.id, parts: s.parts.slice(0, 3).map((p) => p.id) });
            }}
          >
            {scenes.some((s) => s.group)
              ? [...new Set(scenes.map((s) => s.group ?? 'Other'))].sort().map((grp) => (
                <optgroup key={grp} label={t(grp)}>
                  {scenes.filter((s) => (s.group ?? 'Other') === grp).sort((a, b) => a.label.localeCompare(b.label)).map((s) => <option key={s.id} value={s.id}>{t(s.label)}</option>)}
                </optgroup>
              ))
              : scenes.map((s) => <option key={s.id} value={s.id}>{t(s.label)}</option>)}
          </select>
        </div>
      )}
      <div className="scene-editor-body">
        {picture && (
          <div className="scene-editor-preview">
            <SceneView scene={picture} />
          </div>
        )}
        <div className="scene-editor-parts" role="group" aria-label={t(partsLabel)}>
          <span className="field-label">{t(partsLabel)}</span>
          {current.parts.map((p) => (
            <label key={p.id} className="scene-editor-part">
              <input type="checkbox" checked={parts.includes(p.id)} onChange={() => toggle(p.id)} />
              <span>{p.label}</span>
            </label>
          ))}
          <span className="field-hint">{t('{n} chosen', { n: parts.length })}</span>
        </div>
      </div>
    </fieldset>
  );
}
